// The trigger's refusal lines (sweep N5, landing run 1), in one home, so every
// surface that says why a start was refused reads the same words.

// blockingCaption words the 409 trigger.founder_action_pending refusal line
// (manualTriggerErrorLine), its only reader since 0051 slice 2 deleted the
// product page's "Next analysis" panel whose Run caption it once was. The
// wording is the founder's D5 round's (not ruled), so this function only
// moves it.
export function blockingCaption(questions: number, decisions: number): string {
  const q = questions > 0 ? `question${questions === 1 ? "" : "s"}` : "";
  const and = questions > 0 && decisions > 0 ? " and " : "";
  const d = decisions > 0 ? `pending decision${decisions === 1 ? "" : "s"}` : "";
  return `answer your open ${q}${and}${d} first`;
}

// retryAfterPhrase turns a Retry-After header into the words after "Try again
// in". The server sends integer seconds (middleware/ratelimit.go:213, whose
// 6-per-hour runs bucket can mean about 10 minutes; run_lease.go:112). Under 60 s
// it gives "N second(s)"; from 60 s it gives whole minutes, rounded up. Anything
// else (absent, empty, 0, negative, a fraction, an HTTP date) gives "a moment".
export function retryAfterPhrase(retryAfter: string | null | undefined): string {
  const raw = (retryAfter ?? "").trim();
  if (!/^\d+$/.test(raw)) return "a moment";
  const seconds = Number(raw);
  if (seconds <= 0) return "a moment";
  if (seconds < 60) return `${seconds} second${seconds === 1 ? "" : "s"}`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

// 0051 slice 2: FD-1 (c) - no unit word, no time clause.
export const TRIGGER_FALLBACK_LINE = "Couldn't start. Try again.";

// 0051 slice 3: a refused Continue on a settled product (run.no_work_pending).
// The server's own sentence names the retired check-in and Revisit, so the
// client words it with that sentence's own lead, from the system's side (true
// while a quick check still waits; ruled 2026-10-13).
export const NO_WORK_PENDING_LINE = "We're caught up.";

// ALLOWANCE_REFUSED_LINE is FD-1's refused-Continue row (check wording): the one
// line for a Continue the allowance refuses, a server 402 or a page that already
// knows nothing is left. Per FD-10 the count lives in Settings, so the line
// points there and never prints it. One home, so every site reads the same line.
export const ALLOWANCE_REFUSED_LINE = "You've used everything your plan includes. See Settings.";

type TriggerFailure = {
  status?: unknown;
  retryAfter?: unknown;
  envelope?: {
    error?: {
      code?: unknown;
      message?: unknown;
      hints?: { open_questions?: unknown; proposed_decisions?: unknown };
    };
  };
};

// manualTriggerErrorLine words a refused or failed Run click for the founder.
// Its input is the enriched Error useTriggerRun's mutationFn throws (status,
// envelope, retryAfter), or anything else a mutation can reject with.
// It never reads the thrown Error's own (generic) message, nor the server's:
// run.no_work_pending reads NO_WORK_PENDING_LINE (0051 slice 3: the server's
// sentence, middleware/trigger_guard.go:216, names the retired check-in) and
// trigger.unavailable the fallback (its message names the pass,
// run_lease.go:113-114; ruling 2; 0051 slice 2).
// founder_action_pending shows blockingCaption's words, using the
// refusal's own counts (hints.open_questions / proposed_decisions, the
// same TriggerBlockingNodeIDs pass the queues serve). The bar's Continue and
// Try again are offered only when the served step's needs_you is 0 (0051
// slice 2). The server's message there carries "(s)" and "accept/reject".
export function manualTriggerErrorLine(err: unknown): string {
  const e = (err ?? undefined) as TriggerFailure | undefined;
  const status = e?.status;
  if (status === 402) return ALLOWANCE_REFUSED_LINE;
  if (status === 429) {
    const retryAfter = e?.retryAfter;
    return `Rate limited. Try again in ${retryAfterPhrase(typeof retryAfter === "string" ? retryAfter : null)}.`;
  }
  const env = e?.envelope?.error;
  switch (env?.code) {
    case "run.no_work_pending":
      return NO_WORK_PENDING_LINE;
    case "trigger.unavailable":
      return TRIGGER_FALLBACK_LINE;
    case "trigger.founder_action_pending": {
      const questions = positiveCount(env?.hints?.open_questions);
      const decisions = positiveCount(env?.hints?.proposed_decisions);
      if (questions === 0 && decisions === 0) return TRIGGER_FALLBACK_LINE;
      const caption = blockingCaption(questions, decisions);
      return `${caption.charAt(0).toUpperCase()}${caption.slice(1)}.`;
    }
    default:
      return TRIGGER_FALLBACK_LINE;
  }
}

function positiveCount(v: unknown): number {
  return typeof v === "number" && Number.isInteger(v) && v > 0 ? v : 0;
}
